import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { BehaviorSubject } from 'rxjs'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { getLocaleNumberSymbol, NumberSymbol } from '@angular/common'

type Separators = {
  thousand: string
  decimal: string
}

@UntilDestroy()
@Injectable()
/**
 * Service for managing localization values, such as number thousand/decimal separators.
 */
export class LocalizationService {
  private separatorsSubject: BehaviorSubject<Separators>

  private separators: Separators

  /**
   * Observable which will emit a new value when the active decimal separators are changed. Its purpose is to listen to
   * the translateservices onLangChange-event and update the used separators accordingly.
   */
  public get separators$() {
    return this.separatorsSubject.asObservable()
  }

  public get currentSeparators(): Separators {
    return this.separators
  }

  public constructor(private translateService: TranslateService) {
    this.separators = this.getCurrentSeparators(translateService.currentLang() ?? 'en')
    this.separatorsSubject = new BehaviorSubject<Separators>(this.separators)

    translateService.onLangChange.pipe(untilDestroyed(this)).subscribe(({ lang }) => {
      this.separators = this.getCurrentSeparators(lang)
      this.separatorsSubject.next(this.separators)
    })
  }

  private getCurrentSeparators(language: string): Separators {
    const thousand = getLocaleNumberSymbol(language, NumberSymbol.Group)
    const decimal = getLocaleNumberSymbol(language, NumberSymbol.Decimal)

    return { thousand, decimal }
  }
}
